import axios from '../../config/axios.js';
import Alpine from 'alpinejs';
import Quill from 'quill';

const EDITOR_TOOLBAR = [
  [{ header: [false, 1, 2, 3] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['blockquote'],
  ['link', 'image'],
  [{ align: [] }],
  ['clean'],
];

function emptyForm() {
  return {
    title: '',
    slug: '',
    excerpt: '',
    body: '',
    status: 'draft',
    category_id: '',
  };
}

function articleToForm(d) {
  if (!d) return emptyForm();
  return {
    title: d.title || '',
    slug: d.slug || '',
    excerpt: d.excerpt || '',
    body: d.body || '',
    status: d.status || 'draft',
    category_id: d.category_id || d.category?.id || '',
  };
}

function createKasirNewsForm() {
  return {
    saving: false,
    articleId: null,
    indexUrl: '/',
    categories: [],
    form: emptyForm(),
    imageFile: null,
    imagePreview: null,
    removeImage: false,
    quill: null,

    init() {
      const raw = this.$el.getAttribute('data-config');
      if (raw) {
        try {
          const cfg = JSON.parse(raw);
          this.articleId = cfg.articleId || cfg.article?.id || null;
          this.indexUrl = cfg.indexUrl || '/';
          if (cfg.article) {
            Object.assign(this.form, articleToForm(cfg.article));
            if (cfg.article.featured_image_url) {
              this.imagePreview = cfg.article.featured_image_url;
            }
          }
        } catch (e) {
          console.error('kasirNewsForm config', e);
        }
      }

      this.$nextTick(() => this.initEditor());
      this.fetchCategories();

      if (!this.form.body && this.articleId) {
        this.fetchArticle(this.articleId);
      }
    },

    initEditor() {
      if (this.quill || !this.$refs.quillHost) return;

      this.quill = new Quill(this.$refs.quillHost, {
        theme: 'snow',
        placeholder: 'Tulis konten artikel di sini…',
        modules: {
          toolbar: {
            container: EDITOR_TOOLBAR,
            handlers: {
              image: () => this.pickEditorImage(),
            },
          },
        },
      });

      if (this.form.body) {
        this.quill.root.innerHTML = this.form.body;
      }

      this.quill.on('text-change', () => {
        this.form.body = this.quill.root.innerHTML;
      });
    },

    setEditorContent(html) {
      if (!this.quill) return;
      const current = this.quill.root.innerHTML;
      if (current !== html) {
        this.quill.root.innerHTML = html || '';
      }
    },

    pickEditorImage() {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = () => {
        const file = input.files?.[0];
        if (file) this.uploadEditorImage(file);
      };
      input.click();
    },

    async uploadEditorImage(file) {
      if (!file.type.startsWith('image/')) {
        window.toast?.('File harus berupa gambar.', 'error');
        return;
      }
      if (file.size > 4 * 1024 * 1024) {
        window.toast?.('Ukuran gambar maks. 4 MB.', 'error');
        return;
      }

      const fd = new FormData();
      fd.append('image', file);

      try {
        const { data } = await axios.post('/api/v1/kasir/news/upload-image', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const url = data.data?.url;
        if (!url || !this.quill) return;

        const range = this.quill.getSelection(true);
        this.quill.insertEmbed(range.index, 'image', url, 'user');
        this.quill.setSelection(range.index + 1);
        this.form.body = this.quill.root.innerHTML;
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal mengunggah gambar.', 'error');
      }
    },

    async fetchCategories() {
      try {
        const { data } = await axios.get('/api/news/categories');
        this.categories = data.data || [];
      } catch (e) {
        console.error('fetchCategories', e);
      }
    },

    async fetchArticle(id) {
      try {
        const { data } = await axios.get(`/api/v1/kasir/news/${id}`);
        const a = data.data;
        Object.assign(this.form, articleToForm(a));
        if (a?.featured_image_url) this.imagePreview = a.featured_image_url;
        this.setEditorContent(this.form.body);
      } catch (e) {
        window.toast?.('Gagal memuat artikel.', 'error');
      }
    },

    onImageSelect(e) {
      const file = e.target.files?.[0];
      if (!file) return;
      this.imageFile = file;
      this.removeImage = false;
      this.imagePreview = URL.createObjectURL(file);
    },

    clearImage() {
      this.imageFile = null;
      this.imagePreview = null;
      this.removeImage = true;
    },

    bodyHasContent() {
      if (this.quill) {
        return this.quill.getText().trim().length > 0;
      }
      return this.form.body.replace(/<[^>]+>/g, '').trim().length > 0;
    },

    buildFormData(status) {
      if (this.quill) {
        this.form.body = this.quill.root.innerHTML;
      }

      const fd = new FormData();
      const f = { ...this.form, status: status || this.form.status };
      fd.append('title', f.title);
      if (f.slug) fd.append('slug', f.slug);
      if (f.excerpt) fd.append('excerpt', f.excerpt);
      fd.append('body', f.body);
      fd.append('status', f.status);
      if (f.category_id) fd.append('category_id', f.category_id);
      if (this.imageFile) fd.append('featured_image', this.imageFile);
      if (this.removeImage) fd.append('remove_image', '1');
      return fd;
    },

    async save(forceStatus) {
      if (!this.form.title?.trim() || !this.bodyHasContent()) {
        window.toast?.('Judul dan isi artikel wajib diisi.', 'error');
        return;
      }
      this.saving = true;
      const fd = this.buildFormData(forceStatus);
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      try {
        if (this.articleId) {
          fd.append('_method', 'PUT');
          await axios.post(`/api/v1/kasir/news/${this.articleId}`, fd, config);
        } else {
          const { data } = await axios.post('/api/v1/kasir/news', fd, config);
          this.articleId = data.data?.id;
        }
        window.toast?.('Artikel disimpan.', 'success');
        window.location.href = this.indexUrl;
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal menyimpan.', 'error');
      } finally {
        this.saving = false;
      }
    },
  };
}

Alpine.data('kasirNewsForm', createKasirNewsForm);
